import raf from 'raf'
import BindsPropValidator from '../../core/utils/BindsPropValidator'

const BindsAppModes = [
  'fixed',
  'fixed-last',
  'reveal',
  'overlap',
  'flexible'
]

export default {
  props: {
    BindsMode: {
      type: String,
      ...BindsPropValidator('binds-mode', BindsAppModes)
    },
    BindsWaterfall: Boolean,
    BindsScrollbar: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    revealTimer: null,
    revealLastPos: 0,
    manualTick: false,
    BindsApp: {
      options: {
        mode: null,
        waterfall: false,
        flexible: false
      },
      toolbar: {
        element: null,
        titleElement: null,
        height: '0px',
        initialHeight: 0,
        top: 0,
        titleSize: 20,
        hasElevation: true,
        revealActive: false,
        fixedLastActive: false,
        fixedLastHeight: false,
        overlapOff: false
      },
      drawer: {
        initialWidth: 0,
        active: false,
        mode: 'temporary',
        submode: null,
        width: 0,
        right: false
      }
    }
  }),
  provide () {
    return {
      BindsApp: this.BindsApp
    }
  },
  computed: {
    isFixed () {
      return this.bindsMode && this.bindsMode !== 'fixed'
    },
    isDrawerMini () {
      return this.BindsApp.drawer.mode === 'persistent' && this.BindsApp.drawer.submode === 'mini'
    },
    contentPadding () {
      if (this.BindsApp.drawer.active && this.BindsApp.drawer.mode === 'persistent' && this.BindsApp.drawer.submode === 'full') {
        return this.BindsApp.drawer.width
      }
      return 0
    },
    contentStyles () {
      return {
        [`padding-${this.BindsApp.drawer.right ? 'right' : 'left'}`]: this.contentPadding
      }
    },
    containerStyles () {
      let styles = {}

      if (this.isFixed) {
        styles['margin-top'] = this.BindsApp.toolbar.initialHeight + 'px'
      }

      if (this.isDrawerMini) {
        styles[`padding-${this.BindsApp.drawer.right ? 'right' : 'left'}`] = !this.BindsApp.drawer.active ? this.BindsApp.drawer.initialWidth + 'px' : 0
      }

      return styles
    },
    scrollerClasses () {
      if (this.bindsScrollbar) {
        return 'binds-scrollbar'
      }
    },
    appClasses () {
      return {
        'binds-waterfall': this.bindsWaterfall,
        'binds-flexible': this.bindsMode === 'flexible',
        'binds-fixed': this.bindsMode === 'fixed',
        'binds-fixed-last': this.bindsMode === 'fixed-last',
        'binds-reveal': this.bindsMode === 'reveal',
        'binds-overlap': this.bindsMode === 'overlap',
        'binds-drawer-active': this.BindsApp.drawer.active
      }
    }
  },
  watch: {
    bindsMode (mode) {
      this.BindsApp.options.mode = mode
    },
    bindsWaterfall (waterfall) {
      this.BindsApp.options.waterfall = waterfall
      this.setToolbarElevation()
    }
  },
  methods: {
    setToolbarElevation () {
      this.BindsApp.toolbar.hasElevation = !this.bindsWaterfall
    },
    setToolbarTimer (scrollTop) {
      window.clearTimeout(this.revealTimer)

      this.revealTimer = window.setTimeout(() => {
        this.revealLastPos = scrollTop
      }, 100)
    },
    setToolbarMarginAndHeight (margin, height) {
      this.BindsApp.toolbar.top = margin
      this.BindsApp.toolbar.height = height
    },
    getToolbarConstrants ($event) {
      const toolbarHeight = this.BindsApp.toolbar.element.offsetHeight
      const safeAmount = 10
      const threshold = toolbarHeight + safeAmount
      const scrollTop = $event.target.scrollTop

      if (!this.BindsApp.toolbar.initialHeight) {
        this.BindsApp.toolbar.initialHeight = toolbarHeight
      }

      return {
        toolbarHeight,
        safeAmount,
        threshold,
        scrollTop,
        initialHeight: this.BindsApp.toolbar.initialHeight
      }
    },
    handleWaterfallScroll ($event) {
      let { threshold, scrollTop } = this.getToolbarConstrants($event)
      let elevationMark = 4

      if (this.bindsMode === 'reveal') {
        elevationMark = threshold
      }

      this.BindsApp.toolbar.hasElevation = scrollTop >= elevationMark
    },
    handleFlexibleMode ($event) {
      let { scrollTop, initialHeight } = this.getToolbarConstrants($event)
      const toolbar = this.BindsApp.toolbar.element
      const firstRow = toolbar.querySelector('.binds-toolbar-row:first-child')
      const firstRowHeight = firstRow.offsetHeight
      const scrollAmount = initialHeight - scrollTop
      const shouldKeepFlexible = scrollTop < initialHeight - firstRowHeight

      if (firstRowHeight) {
        if (shouldKeepFlexible) {
          toolbar.style.height = scrollAmount + 'px'
        } else {
          toolbar.style.height = firstRowHeight + 'px'
        }
      }

      const titleElement = this.BindsApp.toolbar.titleElement
      if (titleElement) {
        const targetSize = 20
        const initialSize = this.BindsApp.toolbar.titleSize

        if (shouldKeepFlexible) {
          const newSize = Math.max(0, 1 - (scrollTop - initialSize) / (scrollAmount + initialSize + 0.000001)) * (initialSize - targetSize) + targetSize

          titleElement.style.fontSize = newSize + 'px'
        } else {
          titleElement.style.fontSize = '20px'
        }
      }

      let { threshold, toolbarHeight } = this.getToolbarConstrants($event)

      this.setToolbarMarginAndHeight(scrollTop - threshold, toolbarHeight)
    },
    handleRevealMode ($event) {
      const { toolbarHeight, safeAmount, threshold, scrollTop } = this.getToolbarConstrants($event)

      this.setToolbarTimer(scrollTop)
      this.setToolbarMarginAndHeight(scrollTop - threshold, toolbarHeight)

      if (scrollTop >= threshold) {
        this.BindsApp.toolbar.revealActive = this.revealLastPos > scrollTop + safeAmount
      } else {
        this.BindsApp.toolbar.revealActive = true
      }
    },
    handleFixedLastMode ($event) {
      let { scrollTop, toolbarHeight, safeAmount } = this.getToolbarConstrants($event)
      const toolbar = this.BindsApp.toolbar.element
      const firstRow = toolbar.querySelector('.binds-toolbar-row:first-child')
      const firstRowHeight = firstRow.offsetHeight

      this.setToolbarTimer(scrollTop)
      this.setToolbarMarginAndHeight(scrollTop - firstRowHeight, toolbarHeight)
      this.BindsApp.toolbar.fixedLastHeight = firstRowHeight

      if (scrollTop >= firstRowHeight) {
        this.BindsApp.toolbar.fixedLastActive = this.revealLastPos > scrollTop + safeAmount
      } else {
        this.BindsApp.toolbar.fixedLastActive = true
      }
    },
    handleOverlapMode ($event) {
      const { toolbarHeight, scrollTop, initialHeight } = this.getToolbarConstrants($event)
      const toolbar = this.BindsApp.toolbar.element
      const firstRow = toolbar.querySelector('.binds-toolbar-row:first-child')
      const firstRowHeight = firstRow.offsetHeight
      const newHeight = initialHeight - scrollTop - (scrollTop * 100 / (initialHeight - firstRowHeight - (firstRowHeight / 1.5)))

      if (firstRowHeight) {
        if (scrollTop < initialHeight - firstRowHeight && newHeight >= firstRowHeight) {
          this.BindsApp.toolbar.overlapOff = false
          toolbar.style.height = newHeight + 'px'
        } else {
          this.BindsApp.toolbar.overlapOff = true
          toolbar.style.height = firstRowHeight + 'px'
        }
      }

      this.setToolbarMarginAndHeight(scrollTop, toolbarHeight)
    },
    handleModeScroll ($event) {
      if (this.bindsMode === 'reveal') {
        this.handleRevealMode($event)
      } else if (this.bindsMode === 'fixed-last') {
        this.handleFixedLastMode($event)
      } else if (this.bindsMode === 'overlap') {
        this.handleOverlapMode($event)
      } else if (this.bindsMode === 'flexible') {
        this.handleFlexibleMode($event)
      }
    },
    handleScroll ($event) {
      if (this.BindsApp.toolbar.element) {
        raf(() => {
          if (this.bindsWaterfall) {
            this.handleWaterfallScroll($event)
          }

          if (this.bindsMode) {
            this.handleModeScroll($event)
          }
        })
      }
    }
  },
  created () {
    this.BindsApp.options.mode = this.bindsMode
    this.BindsApp.options.waterfall = this.bindsWaterfall
    this.setToolbarElevation()
  },
  mounted () {
    const fakeEvent = {
      target: {
        scrollTop: 0
      }
    }

    if (this.bindsMode === 'reveal') {
      this.BindsApp.toolbar.revealActive = true
      this.handleRevealMode(fakeEvent)
    }

    if (this.bindsMode === 'flexible') {
      this.BindsApp.toolbar.revealActive = true
      this.handleFlexibleMode(fakeEvent)
    }

    if (this.bindsMode === 'fixed-last') {
      this.BindsApp.toolbar.fixedLastActive = true
      this.handleFixedLastMode(fakeEvent)
    }

    if (this.bindsMode === 'overlap') {
      this.handleOverlapMode(fakeEvent)
    }
  }
}
